/**
 * ProviderSearchModal
 *
 * Portal-based modal that renders ChangeProviderPopup directly — no parcel loader.
 * Mirrors Angular's ProviderSearchComponent (which was just a wrapper loading
 * the change-provider-popup-parcel via <abyss-parcel>).
 *
 * Close signals:
 *   - ChangeProviderPopup dispatches 'onCloseDialog' when user confirms/cancels
 *   - cancelPopup sessionStorage key (set by the parcel) distinguishes cancel vs confirm
 */

import { Suspense, lazy, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ChangeProviderPopup = lazy(() =>
  import('@src/apps/ChangeProviderPopup/ChangeProviderPopup').then((m) => ({
    default: m.ChangeProviderPopup,
  }))
);

interface ProviderSearchModalProps {
  onClose: (providerSelected: boolean) => void;
}

export const ProviderSearchModal: React.FC<ProviderSearchModalProps> = ({ onClose }) => {
  useEffect(() => {
    // ChangeProviderPopup dispatches this event when the user closes the popup
    const handleClose = () => {
      const cancelled = sessionStorage.getItem('cancelPopup') === 'true';
      onClose(!cancelled);
    };

    window.addEventListener('onCloseDialog', handleClose);
    return () => window.removeEventListener('onCloseDialog', handleClose);
  }, [onClose]);

  return createPortal(
    <div style={styles.overlay}>
      <div style={styles.dialog}>
        <Suspense fallback={null}>
          <ChangeProviderPopup />
        </Suspense>
      </div>
    </div>,
    document.body
  );
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.32)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  dialog: {
    backgroundColor: '#fff',
    borderRadius: 2,
    boxShadow: '0 20px 40px 0 rgba(0,0,0,0.15)',
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
  },
};
